import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home,
  Compass,
  Plus,
  MessageSquare,
  User,
  Video,
  Radio,
  Film,
  Users,
  Search,
  Settings,
  Bell,
  Mic,
  TrendingUp
} from 'lucide-react';

const MobileBottomNav = ({ 
  activeTab, 
  setActiveTab, 
  activeSubTab, 
  setActiveSubTab, 
  unreadNotifications = 0, 
  feedNotificationCount = 0,
  onGoLive,
  messageCount = 0
}) => {
  const [showCreateMenu, setShowCreateMenu] = useState(false);

  const tabs = [
    { 
      id: 'live', 
      icon: Home, 
      label: 'Home', 
      color: '#FE2C55',
      gradient: 'from-[#FE2C55] to-[#FF6B81]'
    },
    { 
      id: 'feed', 
      icon: Compass, 
      label: 'Discover', 
      color: '#00F2FE',
      gradient: 'from-[#00F2FE] to-[#00D4FF]',
      badge: feedNotificationCount
    },
    { 
      id: 'create', 
      icon: Plus, 
      label: '', 
      isCenter: true,
      color: '#FE2C55'
    },
    { 
      id: 'chat', 
      icon: MessageSquare, 
      label: 'Inbox', 
      color: '#34C759',
      gradient: 'from-[#34C759] to-[#30B350]',
      badge: messageCount
    },
    { 
      id: 'profile', 
      icon: User, 
      label: 'Me', 
      color: '#FFFFFF',
      gradient: 'from-[#FFFFFF] to-[#E5E5E5]',
      badge: unreadNotifications
    },
  ];

  const createOptions = [
    { id: 'go-live', icon: Radio, label: 'Go Live', color: '#FF2A2A', action: () => onGoLive?.() },
    { id: 'video-call', icon: Video, label: 'Video Call', color: '#00F0FF', action: () => { setActiveTab('chat'); setActiveSubTab('video'); } },
    { id: 'voice-room', icon: Mic, label: 'Voice Room', color: '#00FF66', action: () => { setActiveTab('chat'); setActiveSubTab('voice'); } },
    { id: 'post', icon: TrendingUp, label: 'Create Post', color: '#FFE800', action: () => { setActiveTab('feed'); } },
  ];

  const handleTabPress = (tab) => {
    if (tab.id === 'create') {
      setShowCreateMenu(!showCreateMenu);
      return;
    }
    setShowCreateMenu(false);
    setActiveTab(tab.id);
    
    // Set default subtab
    const defaultSubTabs = {
      'live': 'home',
      'feed': 'foryou',
      'chat': 'messages',
      'profile': 'settings'
    };
    if (defaultSubTabs[tab.id]) {
      setActiveSubTab(defaultSubTabs[tab.id]);
    }
  };

  return (
    <>
      {/* Create Menu Overlay */}
      <AnimatePresence>
        {showCreateMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => setShowCreateMenu(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.9 }}
              className="fixed bottom-24 left-4 right-4 z-50 grid grid-cols-2 gap-3"
            >
              {createOptions.map((option) => (
                <motion.button
                  key={option.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    option.action();
                    setShowCreateMenu(false);
                  }}
                  className="flex items-center gap-3 p-4 rounded-2xl bg-[#0A0A0F]/95 backdrop-blur-xl border border-white/10"
                  data-testid={`create-${option.id}`}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${option.color}20` }}
                  >
                    <option.icon className="w-6 h-6" style={{ color: option.color }} />
                  </div>
                  <span className="text-white font-medium text-sm">{option.label}</span>
                </motion.button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom Navigation Bar */}
      <nav 
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#0A0A0F] border-t border-white/5"
        style={{ 
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 20px)'
        }}
        data-testid="bottom-navigation"
      >
        <div className="flex items-center justify-around px-1 pt-2 pb-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;

            // Center Create Button
            if (tab.isCenter) {
              return (
                <motion.button
                  key={tab.id}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleTabPress(tab)}
                  className="relative -mt-5"
                  data-testid="nav-create"
                >
                  <motion.div 
                    animate={{ rotate: showCreateMenu ? 45 : 0 }}
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF2A2A] via-[#FF6B6B] to-[#FFE800] flex items-center justify-center shadow-lg shadow-[#FF2A2A]/30"
                  >
                    <Plus className="w-6 h-6 text-white" strokeWidth={2.5} />
                  </motion.div>
                  {/* Glow ring */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FF2A2A] to-[#FFE800] blur-md opacity-30 -z-10" />
                </motion.button>
              );
            }

            return (
              <motion.button
                key={tab.id}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleTabPress(tab)}
                className="relative flex flex-col items-center justify-center py-1 px-3 min-w-[56px]"
                data-testid={`nav-${tab.id}`}
              >
                {/* Active indicator */}
                {isActive && (
                  <motion.div
                    layoutId="navIndicator"
                    className="absolute -top-0.5 w-8 h-1 rounded-full"
                    style={{ backgroundColor: tab.color }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  />
                )}

                {/* Icon with badge */}
                <div className="relative">
                  <Icon 
                    className="w-6 h-6 transition-colors duration-200"
                    style={{ color: isActive ? tab.color : '#6B7280' }}
                    strokeWidth={isActive ? 2 : 1.5}
                  />
                  
                  {/* Badge */}
                  {tab.badge > 0 && (
                    <motion.span 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 bg-[#FF2A2A] text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-[#0A0A0F]"
                    >
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </motion.span>
                  )}
                </div>

                {/* Label */}
                <span 
                  className="text-[10px] font-medium mt-1 transition-colors duration-200"
                  style={{ color: isActive ? tab.color : '#6B7280' }}
                >
                  {tab.label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
